// PaymentRequestsList.tsx
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  getMerchantPayments,
  authorizePayment,
  PaymentStatus,
  MerchantPayment,
  GetPaymentsInput,
} from "../../redux/merchantPaymentsSlice";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  AppBar,
  Toolbar,
  Alert,
  Snackbar,
  Stack,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Schedule as PendingIcon,
  AttachMoney as MoneyIcon,
  ArrowBack as BackIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

interface PaymentRequestsListProps {
  onBack?: () => void;
}

const PaymentRequestsList: React.FC<PaymentRequestsListProps> = ({
  onBack,
}) => {
  const dispatch = useAppDispatch();
  const { payments, loading, error } = useAppSelector(
    (state) => state.merchantPayments,
  );
  const [selectedPayment, setSelectedPayment] =
    useState<MerchantPayment | null>(null);
  const [approveMode, setApproveMode] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | "all">(
    "all",
  );
  const [currencyFilter, setCurrencyFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"list" | "details" | "approve">(
    "list",
  );

  // Fetch payments on component mount and when filters change
  useEffect(() => {
    fetchPayments();
  }, [statusFilter, currencyFilter]);

  const fetchPayments = () => {
    const input: GetPaymentsInput = {
      skipCount: 0,
      maxResultCount: 100,
      includeExpired: false,
      ...(statusFilter !== "all" && { status: statusFilter }),
      ...(currencyFilter !== "all" && { currency: currencyFilter }),
    };
    dispatch(getMerchantPayments(input));
  };

  // Filter payments based on search term
  const filteredPayments = payments.filter((payment: MerchantPayment) => {
    const matchesSearch =
      searchTerm === "" ||
      payment.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.transactionHash?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const pendingPayments = filteredPayments.filter(
    (payment: MerchantPayment) =>
      payment.status === PaymentStatus.Pending ||
      payment.status === PaymentStatus.Processing,
  );

  const handleApprovePayment = async () => {
    if (!selectedPayment) return;

    try {
      const result = await dispatch(
        authorizePayment(selectedPayment.id),
      ).unwrap();
      if (result.success) {
        setSnackbar({
          open: true,
          message: `Payment for order ${selectedPayment.orderId} has been approved successfully!`,
          severity: "success",
        });
        setApproveMode(false);
        setSelectedPayment(null);
        setViewMode("list");
        fetchPayments(); // Refresh the list
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to approve payment",
        severity: "error",
      });
    }
  };

  const handleViewDetails = (payment: MerchantPayment) => {
    setSelectedPayment(payment);
    setViewMode("details");
    setApproveMode(false);
  };

  const handleStartApprove = (payment: MerchantPayment) => {
    setSelectedPayment(payment);
    setApproveMode(true);
    setViewMode("approve");
  };

  const handleBackToList = () => {
    setViewMode("list");
    setSelectedPayment(null);
    setApproveMode(false);
  };

  const handleCancelApprove = () => {
    setApproveMode(false);
    setViewMode("details");
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.Pending:
        return "warning";
      case PaymentStatus.Processing:
        return "info";
      case PaymentStatus.Completed:
        return "success";
      case PaymentStatus.Failed:
        return "error";
      case PaymentStatus.Expired:
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.Pending:
        return "Pending";
      case PaymentStatus.Processing:
        return "Processing";
      case PaymentStatus.Completed:
        return "Completed";
      case PaymentStatus.Failed:
        return "Failed";
      case PaymentStatus.Expired:
        return "Expired";
      default:
        return "Unknown";
    }
  };

  // Loading state
  if (loading && payments.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Payment Details View
  if (viewMode === "details" && selectedPayment) {
    return (
      <Box sx={{ maxWidth: 800, margin: "0 auto" }}>
        <AppBar
          position="sticky"
          color="default"
          elevation={1}
          sx={{ backgroundColor: "background.paper" }}
        >
          <Toolbar>
            <IconButton edge="start" onClick={handleBackToList} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flex: 1 }}>
              Payment Details
            </Typography>
            {selectedPayment.status === PaymentStatus.Pending && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<ApproveIcon />}
                onClick={() => handleStartApprove(selectedPayment)}
              >
                Approve
              </Button>
            )}
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack spacing={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">
                    Order #{selectedPayment.orderId}
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedPayment.status)}
                    color={getStatusColor(selectedPayment.status)}
                    size="medium"
                  />
                </Box>
                <Divider />
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Amount
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(
                      selectedPayment.amount,
                      selectedPayment.currency,
                    )}
                  </Typography>
                </Box>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Customer Email
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayment.customerEmail || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Created At
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedPayment.creationTime)}
                    </Typography>
                  </Box>
                  <Box gridColumn="span 2">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      To Wallet Address
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {selectedPayment.toWalletAddress}
                    </Typography>
                  </Box>
                  {selectedPayment.transactionHash && (
                    <Box gridColumn="span 2">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Transaction Hash
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {selectedPayment.transactionHash}
                      </Typography>
                    </Box>
                  )}
                  {selectedPayment.description && (
                    <Box gridColumn="span 2">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Description
                      </Typography>
                      <Typography variant="body2">
                        {selectedPayment.description}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {selectedPayment.metadata && (
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Additional Metadata
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "background.default",
                        p: 2,
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                      }}
                    >
                      <pre style={{ margin: 0, overflow: "auto" }}>
                        {JSON.stringify(selectedPayment.metadata, null, 2)}
                      </pre>
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  // Approval View
  if (viewMode === "approve" && selectedPayment) {
    return (
      <Box sx={{ maxWidth: 800, margin: "0 auto" }}>
        <AppBar
          position="sticky"
          color="default"
          elevation={1}
          sx={{ backgroundColor: "background.paper" }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              onClick={handleCancelApprove}
              sx={{ mr: 2 }}
            >
              <BackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flex: 1 }}>
              Approve Payment
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack spacing={3}>
              <Alert severity="warning" icon={<WarningIcon />}>
                <Typography variant="body1" fontWeight="bold">
                  Action Required: Payment Approval
                </Typography>
                <Typography variant="body2">
                  Please review the payment details carefully before approving.
                </Typography>
              </Alert>

              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Order ID
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        #{selectedPayment.orderId}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Amount to Authorize
                      </Typography>
                      <Typography variant="h4" color="primary.main">
                        {formatCurrency(
                          selectedPayment.amount,
                          selectedPayment.currency,
                        )}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Customer
                      </Typography>
                      <Typography variant="body1">
                        {selectedPayment.customerEmail || "N/A"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Created
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(selectedPayment.creationTime)}
                      </Typography>
                    </Box>
                    {selectedPayment.description && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Description
                        </Typography>
                        <Typography variant="body2">
                          {selectedPayment.description}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="body2">
                  Approving this payment will authorize the transfer of funds
                  from the customer's wallet to your merchant wallet. This
                  action is irreversible.
                </Typography>
              </Alert>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={handleCancelApprove}
                  fullWidth
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApprovePayment}
                  startIcon={<ApproveIcon />}
                  fullWidth
                  size="large"
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Confirm Approval"
                  )}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Box>
    );
  }

  // No Payments Illustration
  const NoPaymentsIllustration = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 2,
        textAlign: "center",
      }}
    >
      <PendingIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
      <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
        No Pending Payments
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", maxWidth: "300px" }}
      >
        There are no pending payment requests to approve at this time.
      </Typography>
    </Box>
  );

  // Main List View
  return (
    <Box
      className="payment-requests-list"
      sx={{ maxWidth: 800, margin: "0 auto" }}
    >
      {/* Header */}
      <Box sx={{ mb: 3, px: 2 }}>
        <Typography variant="h5" gutterBottom>
          Payment Approvals
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and approve pending payment requests
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, px: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            size="small"
            placeholder="Search by Order ID, Email or Tx Hash..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 2 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as PaymentStatus | "all")
              }
              label="Status"
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value={PaymentStatus.Pending}>Pending</MenuItem>
              <MenuItem value={PaymentStatus.Processing}>Processing</MenuItem>
              <MenuItem value={PaymentStatus.Completed}>Completed</MenuItem>
              <MenuItem value={PaymentStatus.Failed}>Failed</MenuItem>
              <MenuItem value={PaymentStatus.Expired}>Expired</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Currency</InputLabel>
            <Select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
              label="Currency"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="NDEIPI">NDEIPI</MenuItem>
              <MenuItem value="USDC">USDC</MenuItem>
              <MenuItem value="USDT">USDT</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchPayments} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Stats Summary */}
      {pendingPayments.length > 0 && (
        <Box sx={{ mb: 3, px: 2 }}>
          <Alert severity="info" icon={<PendingIcon />}>
            You have <strong>{pendingPayments.length}</strong> pending payment{" "}
            {pendingPayments.length === 1 ? "request" : "requests"} waiting for
            approval.
          </Alert>
        </Box>
      )}

      {/* Payment Cards */}
      {filteredPayments.length === 0 && !loading ? (
        <NoPaymentsIllustration />
      ) : (
        <Box sx={{ px: 2 }}>
          {filteredPayments.map((payment: MerchantPayment) => (
            <Card
              key={payment.id}
              sx={{
                mb: 2,
                border:
                  payment.status === PaymentStatus.Pending
                    ? "2px solid"
                    : "1px solid",
                borderColor:
                  payment.status === PaymentStatus.Pending
                    ? "warning.main"
                    : "divider",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 3,
                },
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  mb={2}
                >
                  <Box>
                    <Typography variant="h6" component="h3" gutterBottom>
                      Order #{payment.orderId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(payment.creationTime)}
                    </Typography>
                  </Box>
                  <Chip
                    icon={
                      payment.status === PaymentStatus.Pending ? (
                        <PendingIcon />
                      ) : payment.status === PaymentStatus.Completed ? (
                        <ApproveIcon />
                      ) : (
                        <RejectIcon />
                      )
                    }
                    label={getStatusLabel(payment.status)}
                    color={getStatusColor(payment.status)}
                    size="small"
                  />
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Amount
                    </Typography>
                    <Typography variant="h5" color="primary.main">
                      {formatCurrency(payment.amount, payment.currency)}
                    </Typography>
                  </Box>
                  {payment.customerEmail && (
                    <Box textAlign="right">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Customer
                      </Typography>
                      <Typography variant="body2">
                        {payment.customerEmail}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {payment.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {payment.description}
                  </Typography>
                )}

                {payment.transactionHash && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Tx: {payment.transactionHash.substring(0, 20)}...
                    </Typography>
                  </Box>
                )}
              </CardContent>

              <CardActions
                sx={{ justifyContent: "flex-end", pt: 0, pb: 2, px: 2 }}
              >
                <Button size="small" onClick={() => handleViewDetails(payment)}>
                  View Details
                </Button>
                {payment.status === PaymentStatus.Pending && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ApproveIcon />}
                    onClick={() => handleStartApprove(payment)}
                    color="primary"
                  >
                    Approve
                  </Button>
                )}
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentRequestsList;
